import {
  AuthService,
  CacheService,
  DiscoveryService,
  LoggerService,
  RootConfigService,
} from '@backstage/backend-plugin-api';
import {
  getAllEscalationPolicies,
  getChangeEvents,
  getIncidents,
  getOncallUsers,
  getServiceById,
  getServiceByIntegrationKey,
  getServiceStandards,
  getServiceMetrics,
  getAllServices,
  getAllTeams,
  getFilteredServices,
  loadPagerDutyEndpointsFromConfig,
  createServiceIntegration,
  getServiceRelationshipsById,
  addServiceRelationsToService,
  removeServiceRelationsFromService,
} from '../apis/pagerduty';
import { createAutoMatchRunner } from '../services/autoMatchRunner';
import { AutoMatchJobRegistry } from '../services/autoMatchJobs';
import {
  HttpError,
  PagerDutyChangeEventsResponse,
  PagerDutyIncidentsResponse,
  PagerDutyOnCallUsersResponse,
  PagerDutyServiceResponse,
  PagerDutyServiceStandardsResponse,
  PagerDutyServiceMetricsResponse,
  PagerDutyServicesResponse,
  PagerDutyEntityMapping,
  PagerDutyEntityMappingsResponse,
  PagerDutyService,
  PagerDutyServiceDependency,
  PagerDutySetting,
} from '@pagerduty/backstage-plugin-common';
import { loadAuthConfig } from '../auth/auth';
import {
  PagerDutyBackendStore,
  RawDbEntityResultRow,
} from '../db/PagerDutyBackendDatabase';
import { CustomFieldsController } from './customFieldsController';
import * as express from 'express';
import Router from 'express-promise-router';
import type { CatalogApi, GetEntitiesResponse } from '@backstage/catalog-client';

import * as MappingsController from '../controllers/mappings-controller';
import * as CatalogEntityUtils from '../utils/catalog-entity';
import { MiddlewareFactory } from '@backstage/backend-defaults/rootHttpRouter';

/**
 * Setting key for the org-wide custom field data sync toggle.
 * Stored in the `pagerduty_settings` table with value 'enabled' or 'disabled'.
 */
const DATA_SYNC_SETTING_ID = 'settings::data-sync';

export interface RouterOptions {
  logger: LoggerService;
  config: RootConfigService;
  store: PagerDutyBackendStore;
  discovery: DiscoveryService;
  auth: AuthService;
  catalogApi?: CatalogApi;
  cache: CacheService;
}

export type Annotations = {
  'pagerduty.com/integration-key': string;
  'pagerduty.com/service-id': string;
  'pagerduty.com/account': string;
};

export async function buildEntityMappingsResponse(
  entityMappings: RawDbEntityResultRow[],
  componentEntitiesDict: Record<
    string,
    {
      ref: string;
      name: string;
    }
  >,
  componentEntities: GetEntitiesResponse,
  pagerDutyServices: PagerDutyService[],
): Promise<PagerDutyEntityMappingsResponse> {
  const result: PagerDutyEntityMappingsResponse = {
    mappings: [],
  };

  pagerDutyServices.forEach(service => {
    // Check for service mapping annotation in any entity config file and get the entity ref
    const entityRef = componentEntitiesDict[service.id]?.ref;
    const entityName = componentEntitiesDict[service.id]?.name;

    // Check if the service is mapped to an entity in the database
    const entityMapping = entityMappings.find(
      mapping => mapping.serviceId === service.id,
    );

    if (entityMapping) {
      if (entityRef === undefined) {
        if (
          entityMapping.entityRef === '' ||
          entityMapping.entityRef === undefined
        ) {
          result.mappings.push({
            entityRef: '',
            entityName: '',
            integrationKey: entityMapping.integrationKey,
            serviceId: entityMapping.serviceId,
            status: 'NotMapped',
            serviceName: service.name,
            team: service.teams?.[0]?.name ?? '',
            escalationPolicy:
              service.escalation_policy !== undefined
                ? service.escalation_policy.name
                : '',
            serviceUrl: service.html_url,
            account: service.account,
          });
        } else {
          const entityRefName =
            componentEntities.items.find(
              entity =>
                `${entity.kind}:${entity.metadata.namespace}/${entity.metadata.name}`.toLowerCase() ===
                entityMapping.entityRef,
            )?.metadata.name ?? '';

          result.mappings.push({
            entityRef: entityMapping.entityRef,
            entityName: entityRefName,
            serviceId: entityMapping.serviceId,
            integrationKey: entityMapping.integrationKey,
            status: 'OutOfSync',
            serviceName: service.name,
            team: service.teams?.[0]?.name ?? '',
            escalationPolicy:
              service.escalation_policy !== undefined
                ? service.escalation_policy.name
                : '',
            serviceUrl: service.html_url,
            account: service.account,
          });
        }
      } else if (entityRef !== entityMapping.entityRef) {
        const entityRefName =
          componentEntities.items.find(
            entity =>
              `${entity.kind}:${entity.metadata.namespace}/${entity.metadata.name}`.toLowerCase() ===
              entityMapping.entityRef,
          )?.metadata.name ?? '';

        result.mappings.push({
          entityRef:
            entityMapping.entityRef !== '' ? entityMapping.entityRef : '',
          entityName: entityMapping.entityRef !== '' ? entityRefName : '',
          serviceId: entityMapping.serviceId,
          integrationKey: entityMapping.integrationKey,
          status: 'OutOfSync',
          serviceName: service.name,
          team: service.teams?.[0]?.name ?? '',
          escalationPolicy:
            service.escalation_policy !== undefined
              ? service.escalation_policy.name
              : '',
          serviceUrl: service.html_url,
          account: service.account,
        });
      } else if (entityRef === entityMapping.entityRef) {
        result.mappings.push({
          entityRef:
            entityMapping.entityRef !== '' ? entityMapping.entityRef : '',
          entityName: entityMapping.entityRef !== '' ? entityName : '',
          serviceId: entityMapping.serviceId,
          integrationKey: entityMapping.integrationKey,
          status: 'InSync',
          serviceName: service.name,
          team: service.teams?.[0]?.name ?? '',
          escalationPolicy:
            service.escalation_policy !== undefined
              ? service.escalation_policy.name
              : '',
          serviceUrl: service.html_url,
          account: service.account,
        });
      }
    } else {
      const backstageVendorId = 'PRO19CT';
      const backstageIntegrationKey =
        service.integrations?.find(
          integration => integration.vendor?.id === backstageVendorId,
        )?.integration_key ?? '';

      if (entityRef !== undefined) {
        result.mappings.push({
          entityRef: entityRef,
          entityName: entityName,
          serviceId: service.id,
          integrationKey: backstageIntegrationKey,
          status: 'InSync',
          serviceName: service.name,
          team: service.teams?.[0]?.name ?? '',
          escalationPolicy:
            service.escalation_policy !== undefined
              ? service.escalation_policy.name
              : '',
          serviceUrl: service.html_url,
          account: service.account,
        });
      } else {
        result.mappings.push({
          entityRef: '',
          entityName: '',
          serviceId: service.id,
          integrationKey: backstageIntegrationKey,
          status: 'NotMapped',
          serviceName: service.name,
          team: service.teams?.[0]?.name ?? '',
          escalationPolicy:
            service.escalation_policy !== undefined
              ? service.escalation_policy.name
              : '',
          serviceUrl: service.html_url,
          account: service.account,
        });
      }
    }
  });

  const sortedResult = result.mappings.sort((a, b) => {
    if (a.serviceName! < b.serviceName!) {
      return -1;
    } else if (a.serviceName! > b.serviceName!) {
      return 1;
    }
    return 0;
  });

  result.mappings = sortedResult;

  return result;
}

export async function createRouter(
  options: RouterOptions,
): Promise<express.Router> {
  const { logger, config, store, catalogApi, cache } = options;

  if (!catalogApi) {
    throw new Error('Catalog API is required to start the PagerDuty plugin backend');
  }

  if (!cache) {
    throw new Error('Cache service is required to start the PagerDuty plugin backend');
  }

  // Get authentication Config
  await loadAuthConfig(config, logger);

  // Get optional PagerDuty custom endpoints from config
  loadPagerDutyEndpointsFromConfig(config, logger);

  // Create the router
  const router = Router();
  router.use(express.json());

  const runAutoMatch = createAutoMatchRunner(catalogApi, cache);
  const autoMatchJobs = new AutoMatchJobRegistry(cache, runAutoMatch);

  // Initialize controllers
  const customFieldsController = new CustomFieldsController({
    logger,
    store,
  });

  // DELETE /dependencies/service/:serviceId
  router.delete(
    '/dependencies/service/:serviceId',
    async (request, response) => {
      try {
        // Get the serviceId from the request parameters
        const serviceId = request.params.serviceId || '';
        const account = (request.query.account as string) || '';

        if (serviceId === '') {
          response
            .status(400)
            .json(
              "Bad Request: ':serviceId' must be provided as part of the path",
            );
          return;
        }

        const dependencies: string[] =
          Object.keys(request.body).length === 0 ? [] : request.body;
        if (!dependencies || dependencies.length === 0) {
          response
            .status(400)
            .json(
              "Bad Request: 'dependencies' must be provided as part of the request body",
            );
          return;
        }

        const serviceRelations: PagerDutyServiceDependency[] = [];

        dependencies.forEach(async dependency => {
          serviceRelations.push({
            supporting_service: {
              id: dependency,
              type: 'service',
            },
            dependent_service: {
              id: serviceId,
              type: 'service',
            },
          });
        });

        await removeServiceRelationsFromService(serviceRelations, account);

        response.sendStatus(200);
      } catch (error) {
        if (error instanceof HttpError) {
          logger.error(
            `Error occurred while processing request: ${error.message}`,
          );
          response.status(error.status).json({
            errors: [`${error.message}`],
          });
        }
      }
    },
  );

  // POST /dependencies/service/:serviceId
  router.post('/dependencies/service/:serviceId', async (request, response) => {
    try {
      // Get the serviceId from the request parameters
      const serviceId = request.params.serviceId || '';
      const account = (request.query.account as string) || '';

      if (serviceId === '') {
        response
          .status(400)
          .json(
            "Bad Request: ':serviceId' must be provided as part of the path",
          );
        return;
      }

      const dependencies: string[] =
        Object.keys(request.body).length === 0 ? [] : request.body;
      if (!dependencies || dependencies.length === 0) {
        response
          .status(400)
          .json(
            "Bad Request: 'dependencies' must be provided as part of the request body",
          );
        return;
      }

      const serviceRelations: PagerDutyServiceDependency[] = [];

      dependencies.forEach(async dependency => {
        serviceRelations.push({
          supporting_service: {
            id: dependency,
            type: 'service',
          },
          dependent_service: {
            id: serviceId,
            type: 'service',
          },
        });
      });

      await addServiceRelationsToService(serviceRelations, account);

      response.sendStatus(200);
    } catch (error) {
      if (error instanceof HttpError) {
        logger.error(
          `Error occurred while processing request: ${error.message}`,
        );
        response.status(error.status).json({
          errors: [`${error.message}`],
        });
      }
    }
  });

  router.get('/dependencies/service/:serviceId', async (request, response) => {
    try {
      const serviceId = request.params.serviceId;
      const account = (request.query.account as string) || '';

      if (serviceId) {
        const serviceRelationships: PagerDutyServiceDependency[] =
          await getServiceRelationshipsById(serviceId, account);

        if (serviceRelationships) {
          response.json({
            relationships: serviceRelationships,
          });
        }
      } else {
        response
          .status(400)
          .json(
            "Bad Request: ':serviceId' must be provided as part of the path",
          );
      }
    } catch (error) {
      if (error instanceof HttpError) {
        response.status(error.status).json({
          errors: [`${error.message}`],
        });
      }
    }
  });

  // GET /catalog/entity/:entityRef
  router.get(
    '/catalog/entity/:type/:namespace/:name',
    async (request, response) => {
      const type = request.params.type;
      const namespace = request.params.namespace;
      const name = request.params.name;

      try {
        if (type && namespace && name) {
          const entityRef = `${type}:${namespace}/${name}`.toLowerCase();
          const foundEntity = await catalogApi?.getEntityByRef(entityRef);

          if (foundEntity) {
            response.json(
              foundEntity.metadata.annotations?.['pagerduty.com/service-id'],
            );
          } else {
            response.status(404);
          }
        } else {
          response
            .status(400)
            .json(
              "Bad Request: ':entityRef' must be provided as part of the path",
            );
        }
      } catch (error) {
        if (error instanceof HttpError) {
          response.status(error.status).json({
            errors: [`${error.message}`],
          });
        }
      }
    },
  );

  // POST /settings
  router.post('/settings', async (request, response) => {
    try {
      // Get the serviceId from the request parameters
      const settings: PagerDutySetting[] = request.body;

      // For each setting, update or insert the value in the database
      await Promise.all(
        settings.map(async setting => {
          if (setting.id === undefined || setting.value === undefined) {
            response
              .status(400)
              .json("Bad Request: 'id' and 'value' are required");
            return;
          }

          if (!isValidSettingValue(setting.id, setting.value)) {
            response
              .status(400)
              .json(`Bad Request: '${setting.value}' is not a valid value for setting '${setting.id}'`);
            return;
          }

          await store.updateSetting(setting);
        }),
      );

      response.sendStatus(200);
    } catch (error) {
      if (error instanceof HttpError) {
        logger.error(
          `Error occurred while processing request: ${error.message}`,
        );
        response.status(error.status).json({
          errors: [`${error.message}`],
        });
      }
    }
  });

  // GET /settings/:settingId
  router.get('/settings/:settingId', async (request, response) => {
    try {
      // Get param from the request
      const settingId = request.params.settingId;

      // Find setting by id
      const setting = await store.findSetting(settingId);

      if (!setting) {
        response.status(404).json({});
        return;
      }

      response.json(setting);
    } catch (error) {
      if (error instanceof HttpError) {
        response.status(error.status).json({
          errors: [`${error.message}`],
        });
      }
    }
  });

  function isValidSettingValue(id: string, value: string): boolean {
    if (id === DATA_SYNC_SETTING_ID) {
      return value === 'enabled' || value === 'disabled';
    }

    return (
      value === 'backstage' ||
      value === 'pagerduty' ||
      value === 'both' ||
      value === 'disabled'
    );
  }

  // POST /custom-fields
  router.post('/custom-fields', async (request, response) => {
    await customFieldsController.createCustomField(request, response);
  });

  // GET /custom-fields
  router.get('/custom-fields', async (request, response) => {
    await customFieldsController.getCustomFields(request, response);
  });

  // PUT /custom-fields/:id
  router.put('/custom-fields/:id', async (request, response) => {
    await customFieldsController.updateCustomField(request, response);
  });

  // PATCH /custom-fields/:id/enabled
  router.patch('/custom-fields/:id/enabled', async (request, response) => {
    await customFieldsController.toggleCustomFieldEnabled(request, response);
  });

  // DELETE /custom-fields/:id
  router.delete('/custom-fields/:id', async (request, response) => {
    await customFieldsController.deleteCustomField(request, response);
  });

  // POST /custom-fields/sync
  router.post('/custom-fields/sync', async (request, response) => {
    await customFieldsController.syncCustomFieldValues(request, response);
  });

  // POST /custom-fields/sync-logs
  router.post('/custom-fields/sync-logs', async (request, response) => {
    await customFieldsController.createSyncLog(request, response);
  });

  // GET /custom-fields/sync-logs
  router.get('/custom-fields/sync-logs', async (request, response) => {
    await customFieldsController.getSyncLogs(request, response);
  });

  // POST /mapping/entity
  router.post('/mapping/entity', async (request, response) => {
    try {
      // Get the serviceId from the request parameters
      const entity: PagerDutyEntityMapping = request.body;

      if (!entity.serviceId) {
        response
          .status(400)
          .json(
            "Bad Request: 'serviceId' must be provided as part of the request body",
          );
        return;
      }

      // Get all the entity mappings from the database
      const entityMappings = await store.getAllEntityMappings();
      const oldMapping = entityMappings.find(
        mapping => mapping.serviceId === entity.serviceId,
      );

      // in case a mapping is defined and no integration exists,
      // we need to create one
      if (
        entity.entityRef !== '' &&
        (entity.integrationKey === '' || entity.integrationKey === undefined)
      ) {
        const backstageVendorId = 'PRO19CT';
        // check for existing integration key on service
        const service = await getServiceById(
          entity.serviceId,
          entity.account,
          cache,
        );
        const backstageIntegration = service.integrations?.find(
          integration => integration.vendor?.id === backstageVendorId,
        );

        if (!backstageIntegration) {
          // If an integration does not exist for service,
          // create it in PagerDuty
          const integrationKey = await createServiceIntegration({
            serviceId: entity.serviceId,
            vendorId: backstageVendorId,
            account: entity.account,
            cache,
          });

          entity.integrationKey = integrationKey;
        } else {
          entity.integrationKey = backstageIntegration.integration_key;
        }
      }

      const entityMappingId = await store.insertEntityMapping(entity);

      // Refresh new and old entity unless they are empty strings
      if (entity.entityRef !== '') {
        // force refresh of new entity
        await catalogApi?.refreshEntity(entity.entityRef);
      }

      if (oldMapping && oldMapping.entityRef !== '') {
        // force refresh of old entity
        await catalogApi?.refreshEntity(oldMapping.entityRef);
      }

      response.json({
        id: entityMappingId,
        entityRef: entity.entityRef,
        integrationKey: entity.integrationKey,
        serviceId: entity.serviceId,
        status: entity.status,
        account: entity.account,
      });
    } catch (error) {
      if (error instanceof HttpError) {
        logger.error(
          `Error occurred while processing request: ${error.message}`,
        );
        response.status(error.status).json({
          errors: [`${error.message}`],
        });
      } else {
        logger.error(
          `Unexpected error occurred while processing request: ${error}`,
        );
        response.status(500).json({
          errors: [error instanceof Error ? error.message : String(error)],
        });
      }
    }
  });

  // POST /mapping/entities/bulk
  router.post('/mapping/entities/bulk', async (request, response) => {
    try {
      const { mappings } = request.body;

      if (!Array.isArray(mappings)) {
        response.status(400).json({
          error: "Bad Request: 'mappings' must be an array",
        });
        return;
      }

      const existingMappings = await store.getAllEntityMappings();
      const existingServiceIds = new Set(
        existingMappings.map(m => m.serviceId),
      );

      const newMappings: PagerDutyEntityMapping[] = [];
      const skipped: PagerDutyEntityMapping[] = [];
      const errors = [];

      for (const entity of mappings) {
        if (!entity.serviceId) {
          errors.push({
            entityRef: entity.entityRef,
            error: 'Missing serviceId',
          });
          continue;
        }

        if (existingServiceIds.has(entity.serviceId)) {
          skipped.push(entity);
          continue;
        }

        if (
          entity.entityRef !== '' &&
          (entity.integrationKey === '' || entity.integrationKey === undefined)
        ) {
          try {
            const backstageVendorId = 'PRO19CT';
            const service = await getServiceById(
              entity.serviceId,
              entity.account,
              cache,
            );
            const backstageIntegration = service.integrations?.find(
              integration => integration.vendor?.id === backstageVendorId,
            );

            if (!backstageIntegration) {
              const integrationKey = await createServiceIntegration({
                serviceId: entity.serviceId,
                vendorId: backstageVendorId,
                account: entity.account,
                cache,
              });

              entity.integrationKey = integrationKey;
            } else {
              entity.integrationKey = backstageIntegration.integration_key;
            }
          } catch (error) {
            errors.push({
              entityRef: entity.entityRef,
              serviceId: entity.serviceId,
              error:
                error instanceof Error
                  ? `Failed to create integration: ${error.message}`
                  : 'Failed to create integration',
            });
            continue;
          }
        }

        newMappings.push(entity);
      }

      let insertedIds: string[] = [];
      if (newMappings.length > 0) {
        try {
          insertedIds = await store.bulkInsertEntityMappings(newMappings);

          await Promise.all(
            newMappings.map(async entity => {
              if (entity.entityRef !== '') {
                await catalogApi?.refreshEntity(entity.entityRef);
              }
            }),
          );
        } catch (error) {
          logger.error(`Bulk insert failed: ${error}`);
          response.status(500).json({
            errors: ['Bulk insert failed'],
          });
          return;
        }
      }

      const results = newMappings.map((entity, index) => ({
        id: insertedIds[index],
        entityRef: entity.entityRef,
        integrationKey: entity.integrationKey,
        serviceId: entity.serviceId,
        status: entity.status,
        account: entity.account,
      }));

      response.json({
        success: results,
        skipped: skipped.map(entity => ({
          entityRef: entity.entityRef,
          serviceId: entity.serviceId,
          reason: 'Mapping already exists for this service ID',
        })),
        errors: errors,
        total: mappings.length,
        successCount: results.length,
        skippedCount: skipped.length,
        errorCount: errors.length,
      });
    } catch (error) {
      if (error instanceof HttpError) {
        logger.error(
          `Error occurred while processing bulk mappings: ${error.message}`,
        );
        response.status(error.status).json({
          errors: [`${error.message}`],
        });
      } else {
        logger.error(`Unexpected error: ${error}`);
        response.status(500).json({
          errors: ['Internal server error'],
        });
      }
    }
  });

  // DEPRECATED: GET /mapping/entity
  router.get('/mapping/entity', async (_, response) => {
    try {
      // Get all the entity mappings from the database
      const entityMappings = await store.getAllEntityMappings();

      // Get all the entities from the catalog
      const componentEntities = await catalogApi!.getEntities({
        filter: {
          kind: 'Component',
        },
      });

      // Build reference dictionary of componentEntities with serviceId as the key and entity reference and name pair as the value
      const componentEntitiesDict: Record<
        string,
        { ref: string; name: string }
      > = await CatalogEntityUtils.createComponentEntitiesReferenceDict(componentEntities);

      // Get all services from PagerDuty
      const pagerDutyServices = await getAllServices(cache);

      // Build the response object
      const result: PagerDutyEntityMappingsResponse =
        await buildEntityMappingsResponse(
          entityMappings,
          componentEntitiesDict,
          componentEntities,
          pagerDutyServices,
        );

      response.json(result);
    } catch (error) {
      if (error instanceof HttpError) {
        response.status(error.status).json({
          errors: [`${error.message}`],
        });
      }
    }
  });

  router.post('/mapping/entities', MappingsController.getMappingEntities(store, catalogApi, logger));

  // GET /mapping/entity
  router.get(
    '/mapping/entity/:type/:namespace/:name',
    async (request, response) => {
      try {
        // Get the type, namespace and entity name from the request parameters
        const entityType: string = request.params.type || '';
        const entityNamespace: string = request.params.namespace || '';
        const entityName: string = request.params.name || '';

        if (entityType === '' || entityNamespace === '' || entityName === '') {
          response.status(400).json('Required params not specified.');
          return;
        }

        const entityRef =
          `${entityType}:${entityNamespace}/${entityName}`.toLowerCase();

        // Get all the entity mappings from the database
        const entityMapping = await store.findEntityMappingByEntityRef(
          entityRef,
        );

        if (!entityMapping) {
          response
            .status(404)
            .json(`Mapping for entityRef ${entityRef} not found.`);
          return;
        }

        response.json({
          mapping: entityMapping,
        });
      } catch (error) {
        if (error instanceof HttpError) {
          response.status(error.status).json({
            errors: [`${error.message}`],
          });
        }
      }
    },
  );

  // GET /mapping/entity/service/:serviceId
  router.get(
    '/mapping/entity/service/:serviceId',
    async (request, response) => {
      try {
        // Get the type, namespace and entity name from the request parameters
        const serviceId: string = request.params.serviceId ?? '';

        if (serviceId === '') {
          response.status(400).json('Required params not specified.');
          return;
        }

        // Get all the entity mappings from the database
        const entityMapping = await store.findEntityMappingByServiceId(
          serviceId,
        );

        if (!entityMapping) {
          response
            .status(404)
            .json(`Mapping for serviceId ${serviceId} not found.`);
          return;
        }

        response.json({
          mapping: entityMapping,
        });
      } catch (error) {
        if (error instanceof HttpError) {
          response.status(error.status).json({
            errors: [`${error.message}`],
          });
        }
      }
    },
  );

  // POST /mapping/entity/auto-match/start
  router.post('/mapping/entity/auto-match/start', async (request, response) => {
    const threshold: number = request.body.threshold ?? 100;

    if (typeof threshold !== 'number' || threshold < 0 || threshold > 100) {
      response.status(400).json({
        error: 'Invalid threshold. Must be a number between 0 and 100.',
      });
      return;
    }

    const bestOnly: boolean = request.body.bestOnly ?? false;
    const team: string | undefined = request.body.team;
    const account: string | undefined = request.body.account;

    const job = await autoMatchJobs.start({
      threshold,
      bestOnly,
      team,
      account,
    });

    response.status(202).json({
      jobId: job.id,
      status: job.status,
    });
  });

  // GET /mapping/entity/auto-match/:jobId
  router.get('/mapping/entity/auto-match/:jobId', async (request, response) => {
    const jobId = request.params.jobId;
    const job = await autoMatchJobs.get(jobId);

    if (!job) {
      response.status(404).json({
        error: `Auto-match job ${jobId} not found.`,
      });
      return;
    }

    response.json({
      jobId: job.id,
      status: job.status,
      createdAt: job.createdAt,
      completedAt: job.completedAt,
      result: job.result,
      error: job.error,
    });
  });

  // GET /escalation_policies
  router.get('/escalation_policies', async (_, response) => {
    try {
      let escalationPolicyList = await getAllEscalationPolicies();

      // sort the escalation policies by account and name
      escalationPolicyList = escalationPolicyList.sort((a, b) => {
        if (a.account === b.account) {
          return a.name.localeCompare(b.name);
        }
        return a.account!.localeCompare(b.account!);
      });

      const escalationPolicyDropDownOptions = escalationPolicyList.map(
        policy => {
          let policyLabel = policy.name;
          if (policy.account && policy.account !== 'default') {
            policyLabel = `(${policy.account}) ${policy.name}`;
          }

          return {
            label: policyLabel,
            value: policy.id,
          };
        },
      );

      response.json(escalationPolicyDropDownOptions);
    } catch (error) {
      if (error instanceof HttpError) {
        response.status(error.status).json({
          errors: [`${error.message}`],
        });
      }
    }
  });

  // GET /oncall
  router.get('/oncall-users', async (request, response) => {
    try {
      // Get the escalation policy ID from the request parameters with parameter name "escalation_policy_ids[]"
      const escalationPolicyId: string =
        (request.query.escalation_policy_ids as string) || '';
      const account = (request.query.account as string) || '';

      if (escalationPolicyId === '') {
        response
          .status(400)
          .json("Bad Request: 'escalation_policy_ids[]' is required");
        return;
      }

      const oncallUsers = await getOncallUsers(escalationPolicyId, account);
      const onCallUsersResponse: PagerDutyOnCallUsersResponse = {
        users: oncallUsers,
      };

      response.json(onCallUsersResponse);
    } catch (error) {
      if (error instanceof HttpError) {
        response.status(error.status).json({
          errors: [`${error.message}`],
        });
      }
    }
  });

  // GET /services/:serviceId
  router.get('/services/:serviceId', async (request, response) => {
    try {
      // Get the serviceId from the request parameters
      const serviceId: string = request.params.serviceId || '';
      const account = (request.query.account as string) || '';

      if (serviceId === '') {
        response
          .status(400)
          .json(
            "Bad Request: ':serviceId' must be provided as part of the path or 'integration_key' as a query parameter",
          );
        return;
      }

      const service = await getServiceById(serviceId, account, cache);
      const serviceResponse: PagerDutyServiceResponse = {
        service: service,
      };

      response.json(serviceResponse);
    } catch (error) {
      if (error instanceof HttpError) {
        response.status(error.status).json({
          errors: [`${error.message}`],
        });
      }
    }
  });

  // GET /teams?account=:account
  router.get('/teams', async (request, response) => {
    try {
      const account = request.query.account as string | undefined;
      const teams = await getAllTeams(account);
      response.json(teams);
    } catch (error) {
      if (error instanceof HttpError) {
        response.status(error.status).json({
          errors: [`${error.message}`],
        });
      }
    }
  });


  // GET /services - Unified endpoint for all service queries
  // Query params:
  //   - integration_key: fetch service by integration key
  //   - team_id, query, limit, account: fetch filtered services
  //   - no params: fetch all services
  router.get('/services', async (request, response) => {
    try {
      const integrationKey = request.query.integration_key as string | undefined;
      const teamId = request.query.team_id as string | undefined;
      const query = request.query.query as string | undefined;
      const limit = request.query.limit
        ? parseInt(request.query.limit as string, 10)
        : undefined;
      const account = request.query.account as string | undefined;

      // Case 1: Fetch by integration key
      if (integrationKey) {
        const service = await getServiceByIntegrationKey(
          integrationKey,
          account || '',
        );
        const serviceResponse: PagerDutyServiceResponse = {
          service: service,
        };
        response.json(serviceResponse);
        return;
      }

      // Case 2: Fetch filtered services (if team_id, query, or limit provided)
      if (teamId || query || limit) {
        const teamIdsArray: string[] | undefined = teamId ? [teamId] : undefined;
        const services = await getFilteredServices(
          teamIdsArray,
          query,
          limit || 100,
          account,
        );
        response.json(services);
        return;
      }

      // Case 3: Fetch all services (default)
      const services = await getAllServices(cache);
      const servicesResponse: PagerDutyServicesResponse = {
        services: services,
      };
      response.json(servicesResponse);
    } catch (error) {
      if (error instanceof HttpError) {
        response.status(error.status).json({
          errors: [`${error.message}`],
        });
      } else {
        logger.error(
          `Unexpected error occurred while processing request: ${error}`,
        );
        response.status(500).json({
          errors: [error instanceof Error ? error.message : String(error)],
        });
      }
    }
  });

  // POST /services/:serviceId/integration/:vendorId
  router.post(
    '/services/:serviceId/integration/:vendorId',
    async (request, response) => {
      try {
        const serviceId: string = request.params.serviceId || '';
        const vendorId: string = request.params.vendorId || '';
        const account = (request.query.account as string) || '';

        if (serviceId === '' || vendorId === '') {
          response
            .status(400)
            .json(
              "Bad Request: ':serviceId' and ':vendorId' must be provided as part of the path",
            );
          return;
        }

        const integrationKey = await createServiceIntegration({
          serviceId,
          vendorId,
          account,
          cache,
        });

        response.json(integrationKey);
      } catch (error) {
        if (error instanceof HttpError) {
          logger.error(
            `Error occurred while processing request: ${error.message}`,
          );
          response.status(error.status).json({
            errors: [`${error.message}`],
          });
        }
      }
    },
  );

  // GET /services/:serviceId/change-events
  router.get(
    '/services/:serviceId/change-events',
    async (request, response) => {
      try {
        // Get the serviceId from the request parameters
        const serviceId: string = request.params.serviceId || '';
        const account = (request.query.account as string) || '';

        const changeEvents = await getChangeEvents(serviceId, account);
        const changeEventsResponse: PagerDutyChangeEventsResponse = {
          change_events: changeEvents,
        };

        response.json(changeEventsResponse);
      } catch (error) {
        if (error instanceof HttpError) {
          response.status(error.status).json({
            errors: [`${error.message}`],
          });
        }
      }
    },
  );

  // GET /services/:serviceId/incidents
  router.get('/services/:serviceId/incidents', async (request, response) => {
    try {
      // Get the serviceId from the request parameters
      const serviceId: string = request.params.serviceId || '';
      const account = (request.query.account as string) || '';

      const incidents = await getIncidents(serviceId, account);
      const incidentsResponse: PagerDutyIncidentsResponse = {
        incidents,
      };

      response.json(incidentsResponse);
    } catch (error) {
      if (error instanceof HttpError) {
        response.status(error.status).json({
          errors: [`${error.message}`],
        });
      }
    }
  });

  // GET /services/:serviceId/standards
  router.get('/services/:serviceId/standards', async (request, response) => {
    try {
      // Get the serviceId from the request parameters
      const serviceId: string = request.params.serviceId || '';
      const account = (request.query.account as string) || '';

      const serviceStandards = await getServiceStandards(serviceId, account);
      const serviceStandardsResponse: PagerDutyServiceStandardsResponse = {
        standards: serviceStandards,
      };

      response.json(serviceStandardsResponse);
    } catch (error) {
      if (error instanceof HttpError) {
        response.status(error.status).json({
          errors: [`${error.message}`],
        });
      }
    }
  });

  // GET /services/:serviceId/metrics
  router.get('/services/:serviceId/metrics', async (request, response) => {
    try {
      // Get the serviceId from the request parameters
      const serviceId: string = request.params.serviceId || '';
      const account = (request.query.account as string) || '';

      const metrics = await getServiceMetrics(serviceId, account);

      const metricsResponse: PagerDutyServiceMetricsResponse = {
        metrics: metrics,
      };

      response.json(metricsResponse);
    } catch (error) {
      if (error instanceof HttpError) {
        response.status(error.status).json({
          errors: [`${error.message}`],
        });
      }
    }
  });

  // GET /accounts
  router.get('/accounts', async (_, response) => {
    try {
      const accountsConfig = config.getOptional('pagerDuty.accounts') as
        | Array<{
            id: string;
            isDefault?: boolean;
          }>
        | undefined;

      if (accountsConfig && accountsConfig.length > 0) {
        const accounts = accountsConfig.map(account => ({
          id: account.id,
          isDefault: account.isDefault || false,
        }));
        response.status(200).json({ accounts });
      } else {
        response.status(200).json({ accounts: [] });
      }
    } catch (error) {
      logger.error(`Failed to get accounts: ${error}`);
      response.status(500).json({ error: 'Failed to get accounts' });
    }
  });

  // GET /health
  router.get('/health', async (_, response) => {
    response.status(200).json({ status: 'ok' });
  });

  // Add error handler
  router.use(MiddlewareFactory.create({ config, logger }).error());

  // Return the router
  return router;
}
